<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import {
  FileText,
  FolderKanban,
  Search,
  ShieldAlert,
  X,
} from "lucide-vue-next";
import { useWorkspaceStore } from "@/stores/workspace";
import type { SearchResult } from "@/types/models";

const workspace = useWorkspaceStore();
const router = useRouter();
const root = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const query = ref("");
const results = ref<SearchResult[]>([]);
const open = ref(false);
const loading = ref(false);
const error = ref("");
const activeIndex = ref(-1);
let timer = 0;
let generation = 0;

const helper = computed(() =>
  query.value.trim()
    ? `${results.value.length} result${results.value.length === 1 ? "" : "s"}`
    : "Recent projects",
);

async function runSearch(value: string) {
  const current = ++generation;
  loading.value = true;
  error.value = "";
  try {
    const next = await workspace.searchWorkspace(value);
    if (current !== generation) return;
    results.value = next;
    activeIndex.value = next.length ? 0 : -1;
  } catch {
    if (current !== generation) return;
    results.value = [];
    error.value =
      "Search could not be loaded. Check your connection and try again.";
  } finally {
    if (current === generation) loading.value = false;
  }
}

watch(query, (value) => {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => void runSearch(value.trim()), 180);
});

async function show() {
  open.value = true;
  if (!results.value.length) await runSearch(query.value.trim());
}

function clear() {
  query.value = "";
  input.value?.focus();
}

async function choose(result: SearchResult) {
  open.value = false;
  query.value = "";
  await router.push(result.route);
}

function handleKeys(event: KeyboardEvent) {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
    event.preventDefault();
    open.value = true;
    void nextTick(() => input.value?.focus());
    return;
  }
  if (!open.value) return;
  if (event.key === "Escape") {
    event.preventDefault();
    open.value = false;
  } else if (event.key === "ArrowDown" && results.value.length) {
    event.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % results.value.length;
  } else if (event.key === "ArrowUp" && results.value.length) {
    event.preventDefault();
    activeIndex.value =
      (activeIndex.value - 1 + results.value.length) % results.value.length;
  } else if (event.key === "Enter" && activeIndex.value >= 0) {
    event.preventDefault();
    const result = results.value[activeIndex.value];
    if (result) void choose(result);
  }
}

function outside(event: PointerEvent) {
  if (!root.value?.contains(event.target as Node)) open.value = false;
}

onMounted(() => {
  document.addEventListener("keydown", handleKeys);
  document.addEventListener("pointerdown", outside);
});
onBeforeUnmount(() => {
  window.clearTimeout(timer);
  document.removeEventListener("keydown", handleKeys);
  document.removeEventListener("pointerdown", outside);
});
</script>

<template>
  <div ref="root" class="workspace-search">
    <Search :size="17" aria-hidden="true" />
    <input
      ref="input"
      v-model="query"
      role="combobox"
      aria-label="Search workspace"
      aria-autocomplete="list"
      aria-controls="workspace-search-results"
      :aria-expanded="open"
      :aria-activedescendant="
        activeIndex >= 0 ? `workspace-result-${activeIndex}` : undefined
      "
      placeholder="Search projects and work"
      @focus="show"
      @keydown="handleKeys"
    />
    <button v-if="query" type="button" aria-label="Clear search" @click="clear">
      <X :size="15" />
    </button>
    <kbd v-else aria-hidden="true">Ctrl K</kbd>
    <section v-if="open" class="workspace-search__panel">
      <header>
        <span>{{ helper }}</span>
        <span v-if="loading">Searching…</span>
      </header>
      <p v-if="error" class="workspace-search__message" role="alert">
        <ShieldAlert :size="17" />{{ error }}
      </p>
      <div
        v-else-if="results.length"
        id="workspace-search-results"
        role="listbox"
      >
        <button
          v-for="(result, index) in results"
          :id="`workspace-result-${index}`"
          :key="`${result.type}-${result.id}`"
          type="button"
          role="option"
          :aria-selected="activeIndex === index"
          :class="{ active: activeIndex === index }"
          @mouseenter="activeIndex = index"
          @click="choose(result)"
        >
          <FolderKanban
            v-if="result.type === 'project'"
            :size="17"
            aria-hidden="true"
          />
          <FileText v-else :size="17" aria-hidden="true" />
          <span
            ><strong>{{ result.title }}</strong
            ><small>{{ result.context }}</small></span
          >
          <em>{{ result.type }}</em>
        </button>
      </div>
      <p v-else-if="!loading" class="workspace-search__message">
        No accessible work matches this search.
      </p>
      <footer>
        Search respects your current project access and visibility.
      </footer>
    </section>
  </div>
</template>

<style scoped>
.workspace-search {
  position: relative;
  min-width: 0;
  width: min(520px, 46vw);
  flex: 1;
  height: 38px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--sheet-subtle);
  color: var(--text-soft);
}
.workspace-search:focus-within {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px var(--brand-soft);
  background: white;
}
.workspace-search input {
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--text);
}
.workspace-search > button {
  display: grid;
  place-items: center;
  padding: 3px;
  border-radius: 4px;
  background: transparent;
  color: var(--text-soft);
  cursor: pointer;
}
.workspace-search kbd {
  padding: 2px 5px;
  border: 1px solid var(--rule);
  border-radius: 4px;
  background: white;
  font: 10px/1.2 var(--font);
  color: var(--text-soft);
}
.workspace-search__panel {
  position: absolute;
  z-index: 80;
  top: calc(100% + 7px);
  left: 0;
  width: min(620px, calc(100vw - 32px));
  overflow: hidden;
  border: 1px solid var(--rule-strong);
  border-radius: 7px;
  background: white;
  box-shadow: var(--shadow-menu);
}
.workspace-search__panel > header,
.workspace-search__panel > footer {
  display: flex;
  justify-content: space-between;
  padding: 9px 12px;
  background: var(--sheet-subtle);
  color: var(--text-soft);
  font-size: 11px;
}
.workspace-search__panel > footer {
  border-top: 1px solid var(--rule);
}
.workspace-search__panel [role="listbox"] {
  max-height: min(480px, 64vh);
  overflow: auto;
  padding: 5px;
}
.workspace-search__panel [role="option"] {
  width: 100%;
  min-height: 52px;
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 5px;
  background: transparent;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}
.workspace-search__panel [role="option"].active {
  background: var(--brand-soft);
}
.workspace-search__panel [role="option"] span {
  min-width: 0;
  display: grid;
  gap: 2px;
}
.workspace-search__panel strong,
.workspace-search__panel small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.workspace-search__panel small,
.workspace-search__panel em {
  color: var(--text-soft);
  font-size: 11px;
  font-style: normal;
}
.workspace-search__panel em {
  text-transform: capitalize;
}
.workspace-search__message {
  min-height: 72px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: var(--text-soft);
}
@media (max-width: 720px) {
  .workspace-search {
    width: min(100%, 360px);
  }
  .workspace-search kbd {
    display: none;
  }
}
</style>
